import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import BoltIcon from '@mui/icons-material/Bolt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ApartmentIcon from '@mui/icons-material/Apartment';

import {
  RealtimeKind,
  useRealtimeKindsSetting,
  useUpdateRealtimeKinds,
} from '../../api/realtimeKinds';
import {
  useAgentIncidentsSetting,
  useUpdateAgentIncidentsSetting,
} from '../../api/agentSessions';
import {
  useChildTenantsSetting,
  useUpdateChildTenantsSetting,
} from '../../api/tenants';
import AdminPageHeader from './AdminPageHeader';

/**
 * Admin → Entitlements: what an organisation's plan includes, which only
 * FiSci can change.
 *
 * Three groups: live detection feeds (hotspot, lightning, hazard), AI
 * (incident monitoring) and partnership (creating client organisations).
 * They sit together because they are the same kind of decision — features
 * that are metered or contracted and so are not self-service. The page is
 * rendered only for superusers (AdminPage gates on the server's `can_edit`),
 * and every endpoint refuses a non-superuser PATCH regardless. The
 * organisation sees the outcome read-only on its Overview.
 */

const KINDS: {
  kind: RealtimeKind;
  label: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    kind: 'hotspot',
    label: 'Satellite hotspots',
    icon: <LocalFireDepartmentIcon fontSize="small" />,
    description:
      'Satellite fire detections. Collected continuously and free of charge, so enabling this has no per-query cost.',
  },
  {
    kind: 'lightning',
    label: 'Lightning strikes',
    icon: <BoltIcon fontSize="small" />,
    description:
      'Ground-strike detections. The provider charges per query, and enabling this starts polling for the organisation’s projects — so turn it on only when they need it.',
  },
  {
    kind: 'hazard',
    label: 'Hazards incidents & warnings',
    icon: <WarningAmberIcon fontSize="small" />,
    description:
      'Current incidents and warnings from state emergency services (NSW RFS, VIC Emergency, QFES, SA CFS, DFES, ACT ESA), with their warning areas. Free public feeds collected nationally, so enabling this has no per-query cost. Australian coverage only.',
  },
];

/** One entitlement group: a card with its title and explanation at the top. */
function GroupCard({
  title,
  caption,
  children,
}: {
  title: string;
  caption: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      </Box>
      {children}
    </Paper>
  );
}

export default function TenantCapabilitiesManagement() {
  const { data: setting, isLoading } = useRealtimeKindsSetting();
  const update = useUpdateRealtimeKinds();
  const { data: agentSetting } = useAgentIncidentsSetting();
  const updateAgentSetting = useUpdateAgentIncidentsSetting();
  const { data: childTenantsSetting } = useChildTenantsSetting();
  const updateChildTenants = useUpdateChildTenantsSetting();

  const [snackbar, setSnackbar] = useState<string | null>(null);

  // The server decides this; the page is only rendered when it is true, so it
  // is a backstop rather than the gate.
  const canEdit = setting?.can_edit ?? false;

  const handleToggle = (kind: RealtimeKind, label: string, checked: boolean) => {
    update.mutate(
      { [kind]: checked },
      {
        onSuccess: () =>
          setSnackbar(
            checked
              ? `${label} enabled — this organisation's projects can now show them`
              : `${label} disabled — the layers disappear and polling stops`
          ),
      }
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AdminPageHeader
        title="Entitlements"
        description="What this organisation's plan includes. Metered or contracted features, switched on by FiSci."
        audience="fisci"
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        Only FiSci staff see this page. The organisation sees the result read-only on its
        Overview as “Your plan includes”.
      </Alert>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <GroupCard
          title="Live detection feeds"
          caption={
            <>
              Off by default, and the first step of three: a kind must be enabled here before
              a data source for it can be added in Data Management, and it then appears on
              every one of the organisation's projects. Switching it off stops polling for
              all of them. Only metered or contracted feeds are listed — ignition points and
              burnt areas read an ArcGIS layer the organisation already owns, so they cost
              nothing to run and need no switch here; an administrator just adds the layer
              under Data Management.
            </>
          }
        >
          <Box>
            {KINDS.map(({ kind, label, icon, description }) => {
              const configured = setting?.configured_kinds?.[kind] ?? false;
              const enabled = setting?.realtime_kinds?.[kind] ?? false;
              return (
                <Box key={kind} sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        disabled={!canEdit || update.isPending}
                        onChange={(e) => handleToggle(kind, label, e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {icon}
                        <span>{label}</span>
                      </Box>
                    }
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', ml: 6 }}
                  >
                    {description}
                    {enabled && !configured && (
                      <Box
                        component="span"
                        sx={{ display: 'block', color: 'warning.main' }}
                      >
                        Enabled, but no data source of this type exists yet — add one
                        in Data Management for it to produce anything.
                      </Box>
                    )}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {update.isError && (
            <Alert severity="error">
              {(update.error as { message?: string })?.message ??
                'Could not update the live detection feeds'}
            </Alert>
          )}
        </GroupCard>

        <GroupCard
          title="AI"
          caption="Whether this organisation can hand a detection incident to an AI agent."
        >
          {agentSetting ? (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={agentSetting.allow_agent_incidents}
                    disabled={!agentSetting.can_edit || updateAgentSetting.isPending}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      updateAgentSetting.mutate({ allow_agent_incidents: enabled }, {
                        onSuccess: () =>
                          setSnackbar(
                            enabled
                              ? 'AI incident monitoring enabled'
                              : 'AI incident monitoring disabled — the option is now hidden'
                          ),
                        onError: (err) =>
                          setSnackbar(err?.message || 'Could not update the setting'),
                      });
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <SmartToyIcon fontSize="small" />
                    <span>AI incident monitoring</span>
                  </Box>
                }
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', ml: 6 }}
              >
                Lets people hand a detection incident to an AI agent that monitors
                it, runs simulations, notifies subscribers and publishes
                operational briefs. Agent sessions consume metered AI usage.
              </Typography>
              {agentSetting.allow_agent_incidents &&
                !agentSetting.environment_configured && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Enabled, but this environment is not configured for agent
                    management yet — nothing will appear until it is.
                  </Alert>
                )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This environment does not offer AI incident monitoring.
            </Typography>
          )}
        </GroupCard>

        <GroupCard
          title="Partnership"
          caption={
            <>
              For partners and consultancies that run Mitigate for their own clients. When on,
              this organisation's administrators can give the "Create organisations" permission
              to chosen people, who can then create client organisations and work in them.
            </>
          }
        >
          {childTenantsSetting ? (
            <FormControlLabel
              control={
                <Switch
                  checked={childTenantsSetting.allow_child_tenants}
                  disabled={!childTenantsSetting.can_edit || updateChildTenants.isPending}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    updateChildTenants.mutate(enabled, {
                      onSuccess: () =>
                        setSnackbar(
                          enabled
                            ? 'This organisation can now create client organisations'
                            : 'Creating client organisations switched off — existing ones are unaffected'
                        ),
                      onError: (err) => setSnackbar(err?.message || 'Could not update the setting'),
                    });
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ApartmentIcon fontSize="small" />
                  <span>Can create client organisations</span>
                </Box>
              }
            />
          ) : (
            <CircularProgress size={20} />
          )}
        </GroupCard>
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
}
